<template>
  <div>
    <a-tabs
      v-if="!category"
      v-model:activeKey="selectedCategory">
      <a-tab-pane
        v-for="option in categories"
        :key="option.value"
        :tab="option.label" />
    </a-tabs>
    <a-input-search
      v-model:value="query"
      placeholder="Название или UUID ассета"
      allow-clear />
    <a-select
      v-if="surfaceOptions.length && !fixedSurface"
      v-model:value="selectedSurface"
      :options="surfaceOptions"
      class="surface-select"
      placeholder="Выберите тип поверхности" />
    <a-spin :spinning="loading">
      <div
        v-if="filteredAssets.length"
        ref="scrollContainer"
        class="asset-scroll"
        @scroll="onScroll">
        <div
          class="asset-spacer"
          :style="{ height: `${rowCount * rowHeight}px` }">
          <div
            v-for="row in visibleRows"
            :key="row.index"
            class="asset-row"
            :style="{
              top: `${row.index * rowHeight}px`,
              gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
            }">
            <button
              v-for="asset in row.assets"
              :key="asset.uuid"
              type="button"
              class="asset-option"
              :class="{ 'asset-option--selected': selectedUuids.includes(asset.uuid) }"
              :title="asset.name || asset.uuid"
              :disabled="surfaceOptions.length > 0 && !selectedSurface"
              @click="$emit('select', { ...asset, surface: selectedSurface || undefined })">
              <img
                v-if="asset.preview_url"
                :src="asset.preview_url"
                :alt="asset.name || ''"
                loading="lazy"
                decoding="async"
                class="asset-image">
              <div
                v-else
                class="asset-image empty-image">
                Нет превью
              </div>
              <strong>{{ selectedUuids.includes(asset.uuid) ? '✓ ' : '' }}{{ asset.name || asset.uuid }}</strong>
              <small>{{ asset.uuid }}</small>
            </button>
          </div>
        </div>
      </div>
      <a-empty
        v-else-if="!loading"
        :description="query ? 'Ничего не найдено' : 'Ассеты не найдены'" />
    </a-spin>
  </div>
</template>

<script setup>
import {
  computed, nextTick, onBeforeUnmount, onMounted, ref, watch,
} from 'vue';
import { message } from 'ant-design-vue';
import { api } from '@/api';
import { assetCache as cache, pendingAssets as pending } from '@/utils/objectAssetCache';

const props = defineProps({
  category: { type: String, default: null },
  surface: { type: String, default: null },
  fixedSurface: { type: Boolean, default: false },
  apertureType: { type: String, default: null },
  selectedUuids: { type: Array, default: () => [] },
});
defineEmits(['select']);

const categories = [
  { value: 'furniture', label: 'Мебель и 3D-объекты' },
  { value: 'material', label: 'Материалы' },
  { value: 'aperture', label: 'Двери и окна' },
  { value: 'molding', label: 'Молдинги' },
];
const endpoints = {
  furniture: () => api.getFurnitureList(),
  material: () => api.getMaterialsList(),
  aperture: () => api.getAperturesList({ is_hidden: false }),
  molding: () => api.getMoldingsList({ is_hidden: false, is_deprecated: false }),
};
const toAsset = (category, item) => ({
  category,
  uuid: item.uuid,
  name: category === 'material' ? item.meta?.en?.name : item.name,
  preview_url: {
    furniture: item.preview_url || item.top_view_url,
    material: item.meta?.preview?.sphere || item.meta?.preview?.plane,
    aperture: item.previews?.[0]?.url,
    molding: item.previews?.[0]?.url,
  }[category] || null,
  archived: false,
  aperture_type: category === 'aperture' ? item.aperture_type : null,
  molding_type: category === 'molding' ? item.molding_type : null,
});

const rowHeight = 176;
const viewportHeight = 480;
const selectedCategory = ref('furniture');
const currentCategory = computed(() => props.category || selectedCategory.value);
const surfaceOptions = computed(() => {
  if (currentCategory.value === 'material') {
    return [
      { value: 'wall', label: 'Стены' },
      { value: 'floor', label: 'Пол' },
      { value: 'ceiling', label: 'Потолок' },
    ];
  }
  if (currentCategory.value === 'molding') {
    return [
      { value: 'floor', label: 'Напольный' },
      { value: 'ceiling', label: 'Потолочный' },
    ];
  }
  return [];
});
const selectedSurface = ref(props.surface);
const query = ref('');
const assets = ref([]);
const loading = ref(false);
const scrollContainer = ref(null);
const scrollTop = ref(0);
const containerWidth = ref(760);
const columnCount = computed(() => Math.max(1, Math.floor((containerWidth.value + 12) / 167)));
const filteredAssets = computed(() => {
  const search = query.value.trim().toLocaleLowerCase();
  return assets.value.filter((asset) => {
    if (props.apertureType && asset.aperture_type !== props.apertureType) return false;
    if (currentCategory.value === 'molding' && selectedSurface.value
      && ![selectedSurface.value, 'universal'].includes(asset.molding_type)) return false;
    return !search || `${asset.name || ''} ${asset.uuid}`.toLocaleLowerCase().includes(search);
  });
});
const rowCount = computed(() => Math.ceil(filteredAssets.value.length / columnCount.value));
const visibleRows = computed(() => {
  const first = Math.max(0, Math.floor(scrollTop.value / rowHeight) - 2);
  const last = Math.min(rowCount.value, first + Math.ceil(viewportHeight / rowHeight) + 5);
  return Array.from({ length: Math.max(0, last - first) }, (_, offset) => {
    const index = first + offset;
    return {
      index,
      assets: filteredAssets.value.slice(index * columnCount.value, (index + 1) * columnCount.value),
    };
  });
});
let requestId = 0;
let resizeObserver;

const resetScroll = () => {
  scrollTop.value = 0;
  if (scrollContainer.value) scrollContainer.value.scrollTop = 0;
};
const measure = () => {
  containerWidth.value = scrollContainer.value?.clientWidth || 760;
};
const onScroll = (event) => { scrollTop.value = event.target.scrollTop; };
const fetchCategory = (category) => {
  if (cache.has(category)) return Promise.resolve(cache.get(category));
  if (pending.has(category)) return pending.get(category);
  const request = endpoints[category]().then(({ data }) => {
    const uniqueAssets = new Map();
    (Array.isArray(data) ? data : []).forEach((item) => {
      if (item.uuid && !uniqueAssets.has(item.uuid)) {
        uniqueAssets.set(item.uuid, toAsset(category, item));
      }
    });
    return [...uniqueAssets.values()];
  })
    .then((result) => {
      cache.set(category, result);
      return result;
    })
    .finally(() => pending.delete(category));
  pending.set(category, request);
  return request;
};
const loadCategory = async () => {
  const category = currentCategory.value;
  requestId += 1;
  const currentRequest = requestId;
  resetScroll();
  if (cache.has(category)) {
    assets.value = cache.get(category);
    loading.value = false;
    await nextTick();
    measure();
    return;
  }

  loading.value = true;
  assets.value = [];
  try {
    const result = await fetchCategory(category);
    if (currentRequest === requestId) {
      assets.value = result;
      await nextTick();
      measure();
    }
  } catch (error) {
    if (currentRequest === requestId) {
      message.error(error.response?.data?.message || 'Не удалось загрузить ассеты');
    }
  } finally {
    if (currentRequest === requestId) loading.value = false;
  }
};

watch(currentCategory, loadCategory);
watch(currentCategory, () => { selectedSurface.value = props.category ? props.surface : null; });
watch(query, resetScroll);
onMounted(() => {
  resizeObserver = new ResizeObserver(measure);
  if (scrollContainer.value) resizeObserver.observe(scrollContainer.value);
  loadCategory();
  if (!props.category) {
    categories.filter((option) => option.value !== currentCategory.value)
      .forEach((option) => { fetchCategory(option.value).catch(() => {}); });
  }
});
watch(scrollContainer, (element, previous) => {
  if (previous) resizeObserver?.unobserve(previous);
  if (element) resizeObserver?.observe(element);
  measure();
});
onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  requestId += 1;
});
</script>

<style scoped>
.surface-select { width: min(250px, 100%); margin-top: 12px; }
.asset-scroll { max-height: min(480px, 40vh); margin-top: 18px; overflow-y: auto; }
.asset-spacer { position: relative; }
.asset-row {
  position: absolute;
  right: 0;
  left: 0;
  display: grid;
  box-sizing: border-box;
  gap: 12px;
  height: 176px;
  padding-bottom: 12px;
}
.asset-option {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #fff;
  text-align: left;
  cursor: pointer;
}
.asset-option--selected { border-color: #1677ff; background: #e6f4ff; }
.asset-option:hover { border-color: #1677ff; background: #f6faff; }
.asset-image { width: 100%; height: 100px; object-fit: contain; border-radius: 5px; background: #f5f5f5; }
.empty-image { display: grid; place-items: center; color: #999; font-size: 12px; }
.asset-option strong { width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.asset-option small {
  width: 100%;
  overflow: hidden;
  color: #999;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 10px;
}
</style>
